/** Shared OTA editor form (react-hook-form + zod), used by both the create dialog
 *  and the detail page. Covers the OTA scalar fields plus a domains editor
 *  (`useFieldArray`): each row has a domain, a match-type select, a sequence, an
 *  expandable per-domain overrides area (unique_emails / plus_supported tri-state
 *  null|true|false, suppress_emails, contact_email, contact_phone) and a remove
 *  button, plus an "Add domain" button.
 *
 *  The form owns its RHF state and reports `dirty` + a `submit()` up through the
 *  `footer` render-prop so the parent can supply the right chrome (dialog buttons
 *  on create; EditorSaveBar on the detail page). `onSubmit` receives the fully
 *  assembled `OTAWrite` (domains always included = full-array upsert). */
import { zodResolver } from '@hookform/resolvers/zod';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { type ReactNode, useEffect } from 'react';
import {
  Controller,
  useFieldArray,
  useForm,
  type Control,
  type FieldErrors,
} from 'react-hook-form';
import { z } from 'zod';

import {
  FORMAT_OVERRIDE_CHANNELS,
  MATCH_TYPES,
  type FormatOverrideChannel,
  type OTADetail,
  type OTAFormatOverrides,
  type OTAWrite,
} from '@/api/otas';
import { act, type ValueAction } from '@/components/ValueActions/ValueActions';
import {
  plainEmailActions,
  plainPhoneActions,
  urlActions,
} from '@/features/users/userLinks';

import { FieldActionsAdornment, isHttpUrl } from '@/components/ValueActions/FieldActionsAdornment';

/** The actions a form value offers, derived from the LIVE field value: an
 *  empty (or non-URL) value yields none and the adornment disappears rather
 *  than offering a link that goes nowhere. */
const reviewUrlActions = (v: string): ValueAction[] => {
  const url = v.trim();
  if (!url) return [];
  return isHttpUrl(url) ? urlActions(url, 'Open the review page') : [act.copy(url, 'Copy URL')];
};
const emailFieldActions = (v: string): ValueAction[] => (v.trim() ? plainEmailActions(v.trim()) : []);
const phoneFieldActions = (v: string): ValueAction[] => (v.trim() ? plainPhoneActions(v.trim()) : []);
const copyFieldActions = (v: string, label: string): ValueAction[] =>
  v.trim() ? [act.copy(v.trim(), label)] : [];

/** Tri-state override select value. RHF holds the string; we map to null|bool on
 *  submit. 'inherit' means "use the OTA's value". */
const TRISTATE = [
  { value: 'inherit', label: 'Inherit' },
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
] as const;
type TriState = (typeof TRISTATE)[number]['value'];

const triFromBool = (v: boolean | null | undefined): TriState =>
  v === true ? 'true' : v === false ? 'false' : 'inherit';
const boolFromTri = (v: TriState): boolean | null =>
  v === 'true' ? true : v === 'false' ? false : null;

/** Per-channel enforced-format select value; 'default' = no override. */
const FORMAT_CHOICES = [
  { value: 'default', label: 'Default' },
  { value: 'html', label: 'HTML' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'plaintext', label: 'Plain text' },
] as const;
const enforcedFormat = z.enum(['default', 'html', 'markdown', 'plaintext']);

const CHANNEL_LABELS: Record<FormatOverrideChannel, string> = {
  email: 'Email',
  sms: 'SMS',
  whatsapp: 'WhatsApp',
  chat: 'Chat',
  airbnb: 'Airbnb',
};

const domainSchema = z.object({
  id: z.number().nullable(),
  domain: z.string().trim().min(1, 'Domain is required').max(256),
  match_type: z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3)]),
  sequence: z.number(),
  unique_emails: z.enum(['inherit', 'true', 'false']),
  plus_supported: z.enum(['inherit', 'true', 'false']),
  suppress_emails: z.boolean(),
  contact_email: z.string().max(256),
  contact_phone: z.string().max(64),
});

const schema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(256),
  enabled: z.boolean(),
  sequence: z.coerce.number({ invalid_type_error: 'Enter a number' }).int(),
  unique_emails: z.boolean(),
  plus_supported: z.boolean(),
  direct: z.boolean(),
  app_promotion: z.boolean(),
  review_url: z.string().max(512),
  contact_email: z.string().max(256),
  contact_phone: z.string().max(64),
  notes: z.string(),
  format_overrides: z.object({
    email: enforcedFormat,
    sms: enforcedFormat,
    whatsapp: enforcedFormat,
    chat: enforcedFormat,
    airbnb: enforcedFormat,
  }),
  domains: z.array(domainSchema),
});

type FormValues = z.infer<typeof schema>;
type DomainRow = FormValues['domains'][number];

const EMPTY: FormValues = {
  name: '',
  enabled: true,
  sequence: 0,
  unique_emails: false,
  plus_supported: false,
  direct: false,
  app_promotion: false,
  review_url: '',
  contact_email: '',
  contact_phone: '',
  notes: '',
  format_overrides: { email: 'default', sms: 'default', whatsapp: 'default', chat: 'default', airbnb: 'default' },
  domains: [],
};

function toDefaults(ota: OTADetail | null): FormValues {
  if (!ota) return EMPTY;
  return {
    name: ota.name,
    enabled: ota.enabled,
    sequence: ota.sequence ?? 0,
    unique_emails: Boolean(ota.unique_emails),
    plus_supported: Boolean(ota.plus_supported),
    direct: Boolean(ota.direct),
    app_promotion: Boolean(ota.app_promotion),
    review_url: ota.review_url ?? '',
    contact_email: ota.contact_email ?? '',
    contact_phone: ota.contact_phone ?? '',
    notes: ota.notes ?? '',
    format_overrides: Object.fromEntries(
      FORMAT_OVERRIDE_CHANNELS.map((ch) => [ch, ota.format_overrides?.[ch] ?? 'default']),
    ) as FormValues['format_overrides'],
    domains: (ota.domains ?? []).map((d) => ({
      id: d.id,
      domain: d.domain,
      match_type: d.match_type,
      sequence: d.sequence ?? 0,
      unique_emails: triFromBool(d.unique_emails),
      plus_supported: triFromBool(d.plus_supported),
      suppress_emails: Boolean(d.suppress_emails),
      contact_email: d.contact_email ?? '',
      contact_phone: d.contact_phone ?? '',
    })),
  };
}

function toWrite(v: FormValues): OTAWrite {
  const format_overrides: OTAFormatOverrides = {};
  for (const ch of FORMAT_OVERRIDE_CHANNELS) {
    const value = v.format_overrides[ch];
    if (value !== 'default') format_overrides[ch] = value;
  }
  return {
    name: v.name.trim(),
    enabled: v.enabled,
    sequence: v.sequence,
    unique_emails: v.unique_emails,
    plus_supported: v.plus_supported,
    direct: v.direct,
    app_promotion: v.app_promotion,
    review_url: v.review_url.trim() || null,
    contact_email: v.contact_email.trim() || null,
    contact_phone: v.contact_phone.trim() || null,
    notes: v.notes.trim() || null,
    // Full-replace: always sent so reverting a channel to Default clears it.
    format_overrides,
    // Full-array upsert: always send the array so removed rows are deleted.
    domains: v.domains.map((d, i) => ({
      id: d.id,
      domain: d.domain.trim(),
      match_type: d.match_type,
      sequence: i,
      unique_emails: boolFromTri(d.unique_emails),
      plus_supported: boolFromTri(d.plus_supported),
      suppress_emails: d.suppress_emails,
      contact_email: d.contact_email.trim() || null,
      contact_phone: d.contact_phone.trim() || null,
    })),
  };
}

interface FooterCtx {
  dirty: boolean;
  saving: boolean;
  submit: () => void;
  /** Revert the form to the last-seeded record (for a Discard button). */
  reset: () => void;
}

interface Props {
  /** null = create; a record = edit. */
  ota: OTADetail | null;
  canManage: boolean;
  saving: boolean;
  onSubmit: (body: OTAWrite) => void;
  /** Rendered below the form; receives dirty state + the submit handler so the
   *  parent supplies its own chrome (dialog buttons vs. EditorSaveBar). */
  footer: (ctx: FooterCtx) => ReactNode;
}

export function OTAForm({ ota, canManage, saving, onSubmit, footer }: Props) {
  const { control, handleSubmit, reset, formState, watch } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: toDefaults(ota),
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'domains' });

  // Re-seed when the server record changes (incl. after a save → refetch, so the
  // form isn't stuck "dirty").
  useEffect(() => {
    reset(toDefaults(ota));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ota]);

  const submit = handleSubmit((v) => onSubmit(toWrite(v)));
  const err = formState.errors;
  const dis = !canManage;

  const switchField = (name: 'enabled' | 'unique_emails' | 'plus_supported' | 'direct' | 'app_promotion', label: string) => (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <FormControlLabel
          control={
            <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={dis} />
          }
          label={label}
        />
      )}
    />
  );

  return (
    <Box component="form" onSubmit={submit} noValidate>
      <Section title="Basics">
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <TextField
              {...field}
              label="Name"
              size="small"
              fullWidth
              sx={{ mb: 1.5 }}
              disabled={dis}
              error={!!err.name}
              helperText={err.name?.message}
            />
          )}
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
          {switchField('enabled', 'Enabled')}
          {switchField('direct', 'Direct')}
          {switchField('unique_emails', 'Unique emails')}
          {switchField('plus_supported', 'Plus addressing supported')}
          {switchField('app_promotion', 'App promotion')}
        </Box>
        <Controller
          control={control}
          name="sequence"
          render={({ field }) => (
            <TextField
              value={field.value ?? 0}
              onChange={(e) => field.onChange(e.target.value)}
              label="Sequence"
              size="small"
              type="number"
              sx={{ mt: 1.5, width: 200 }}
              disabled={dis}
              error={!!err.sequence}
              helperText={err.sequence?.message ?? 'Match order (lower first).'}
            />
          )}
        />
      </Section>

      <Section title="Contact & links">
        <Controller
          control={control}
          name="review_url"
          render={({ field }) => (
            <TextField
              {...field}
              label="Review URL"
              size="small"
              fullWidth
              sx={{ mb: 1.5 }}
              disabled={dis}
              error={!!err.review_url}
              helperText={err.review_url?.message}
              InputProps={{
                endAdornment: (
                  <FieldActionsAdornment
                    actions={reviewUrlActions(field.value)}
                    label="Review URL actions"
                  />
                ),
              }}
            />
          )}
        />
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Controller
            control={control}
            name="contact_email"
            render={({ field }) => (
              <TextField
                {...field}
                label="Contact email"
                size="small"
                sx={{ flex: 1, minWidth: 220 }}
                disabled={dis}
                error={!!err.contact_email}
                helperText={err.contact_email?.message}
                InputProps={{
                  endAdornment: (
                    <FieldActionsAdornment
                      actions={emailFieldActions(field.value)}
                      label="Contact email actions"
                    />
                  ),
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="contact_phone"
            render={({ field }) => (
              <TextField
                {...field}
                label="Contact phone"
                size="small"
                sx={{ flex: 1, minWidth: 220 }}
                disabled={dis}
                error={!!err.contact_phone}
                helperText={err.contact_phone?.message}
                InputProps={{
                  endAdornment: (
                    <FieldActionsAdornment
                      actions={phoneFieldActions(field.value)}
                      label="Contact phone actions"
                    />
                  ),
                }}
              />
            )}
          />
        </Box>
      </Section>

      <Section title="Notes">
        <Controller
          control={control}
          name="notes"
          render={({ field }) => (
            <TextField
              {...field}
              label="Notes"
              size="small"
              fullWidth
              multiline
              minRows={2}
              disabled={dis}
              helperText="Optional."
            />
          )}
        />
      </Section>

      <Section title="Message format overrides">
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Rendering format this OTA enforces per delivery channel — e.g. an OTA whose
          relay strips all HTML enforces &quot;Plain text&quot; for Email. &quot;Default&quot;
          keeps the channel&apos;s normal format.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {FORMAT_OVERRIDE_CHANNELS.map((ch) => (
            <Controller
              key={ch}
              control={control}
              name={`format_overrides.${ch}`}
              render={({ field }) => (
                <TextField
                  select
                  label={CHANNEL_LABELS[ch]}
                  size="small"
                  sx={{ width: 150 }}
                  disabled={dis}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                >
                  {FORMAT_CHOICES.map((f) => (
                    <MenuItem key={f.value} value={f.value}>
                      {f.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          ))}
        </Box>
      </Section>

      <Section title="Email domains">
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Ordered rules used to attribute an inbound email to this OTA. Per-domain
          overrides inherit the OTA&apos;s value unless set.
        </Typography>
        <Stack spacing={1.5}>
          {fields.map((f, i) => (
            <DomainRowEditor
              key={f.id}
              control={control}
              index={i}
              disabled={dis}
              errorFor={err.domains?.[i]}
              onRemove={() => remove(i)}
              domainLabel={watch(`domains.${i}.domain`)}
            />
          ))}
        </Stack>
        <Button
          startIcon={<AddIcon />}
          size="small"
          sx={{ mt: 1.5 }}
          disabled={dis}
          onClick={() =>
            append({
              id: null,
              domain: '',
              match_type: 0,
              sequence: fields.length,
              unique_emails: 'inherit',
              plus_supported: 'inherit',
              suppress_emails: false,
              contact_email: '',
              contact_phone: '',
            })
          }
        >
          Add domain
        </Button>
      </Section>

      {footer({ dirty: formState.isDirty, saving, submit, reset: () => reset(toDefaults(ota)) })}
    </Box>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary' }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

interface DomainRowProps {
  control: Control<FormValues>;
  index: number;
  disabled: boolean;
  errorFor: FieldErrors<DomainRow> | undefined;
  onRemove: () => void;
  domainLabel: string;
}

function DomainRowEditor({ control, index, disabled, errorFor, onRemove, domainLabel }: DomainRowProps) {
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <Controller
          control={control}
          name={`domains.${index}.domain`}
          render={({ field }) => (
            <TextField
              {...field}
              label="Domain"
              size="small"
              sx={{ flex: 1, minWidth: 220 }}
              disabled={disabled}
              error={!!errorFor?.domain}
              helperText={errorFor?.domain?.message}
              InputProps={{
                endAdornment: (
                  <FieldActionsAdornment
                    actions={copyFieldActions(field.value, 'Copy domain')}
                    label="Domain actions"
                  />
                ),
              }}
            />
          )}
        />
        <Controller
          control={control}
          name={`domains.${index}.match_type`}
          render={({ field }) => (
            <TextField
              select
              label="Match type"
              size="small"
              sx={{ width: 160 }}
              disabled={disabled}
              value={field.value}
              onChange={(e) => field.onChange(Number(e.target.value) as 0 | 1 | 2 | 3)}
            >
              {MATCH_TYPES.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <IconButton aria-label="Remove domain" onClick={onRemove} disabled={disabled} sx={{ mt: 0.5 }}>
          <DeleteOutlineIcon />
        </IconButton>
      </Box>

      <Accordion disableGutters elevation={0} sx={{ mt: 1, '&:before': { display: 'none' }, bgcolor: 'transparent' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0, minHeight: 36 }}>
          <Typography variant="caption" color="text.secondary">
            Overrides{domainLabel ? ` for ${domainLabel}` : ''}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pt: 0 }}>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
            <Controller
              control={control}
              name={`domains.${index}.unique_emails`}
              render={({ field }) => (
                <TextField
                  select
                  label="Unique emails"
                  size="small"
                  sx={{ width: 160 }}
                  disabled={disabled}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value as TriState)}
                >
                  {TRISTATE.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              control={control}
              name={`domains.${index}.plus_supported`}
              render={({ field }) => (
                <TextField
                  select
                  label="Plus supported"
                  size="small"
                  sx={{ width: 160 }}
                  disabled={disabled}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value as TriState)}
                >
                  {TRISTATE.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              control={control}
              name={`domains.${index}.suppress_emails`}
              render={({ field }) => (
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      disabled={disabled}
                    />
                  }
                  label="Suppress emails"
                />
              )}
            />
          </Box>
          <Divider sx={{ mb: 1.5 }} />
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Controller
              control={control}
              name={`domains.${index}.contact_email`}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Contact email"
                  size="small"
                  sx={{ flex: 1, minWidth: 220 }}
                  disabled={disabled}
                  error={!!errorFor?.contact_email}
                  helperText={errorFor?.contact_email?.message}
                  InputProps={{
                    endAdornment: (
                      <FieldActionsAdornment
                        actions={emailFieldActions(field.value)}
                        label="Domain contact email actions"
                      />
                    ),
                  }}
                />
              )}
            />
            <Controller
              control={control}
              name={`domains.${index}.contact_phone`}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Contact phone"
                  size="small"
                  sx={{ flex: 1, minWidth: 220 }}
                  disabled={disabled}
                  error={!!errorFor?.contact_phone}
                  helperText={errorFor?.contact_phone?.message}
                  InputProps={{
                    endAdornment: (
                      <FieldActionsAdornment
                        actions={phoneFieldActions(field.value)}
                        label="Domain contact phone actions"
                      />
                    ),
                  }}
                />
              )}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
